import { useEffect } from 'react';
import { isMaxed } from '@playnets/sim';
import { useUi, type CoachId } from '../store';
import type { GameHandle } from '../game/useGame';

/**
 * Instructions, of which there were none. Playtesting found the game gave a
 * first-time player an empty planet and no idea that mounts were buildable or
 * that the blobs were worth picking up.
 *
 * The rules here: one hint at a time, each pointing at exactly one verb; each
 * dismissed by *doing the thing* rather than by an OK button; and each shown
 * once ever. A hint that survives being obeyed is nagging.
 */
interface Hint {
  id: CoachId;
  text: string;
  /** Should this be showing right now? */
  when: (ctx: Ctx) => boolean;
  gesture?: 'tap-down' | 'drag-up';
  /** Where on the plate the hint sits. Defaults by phase. */
  at?: 'ult' | 'centre';
}

interface Ctx {
  building: boolean;
  hasStructure: boolean;
  juiceOnField: number;
  wave: number;
  ultReady: boolean;
  canUpgrade: boolean;
  hasMaxed: boolean;
}

const HINTS: Hint[] = [
  {
    // The one thing a first-timer cannot work out by looking. It sits low, just
    // above the bar, with a finger that travels from a card up to a mount —
    // the sentence and the picture say the same thing, which is the only way
    // to explain a gesture without a paragraph.
    id: 'build',
    text: 'Drag a gun up onto a ✛',
    when: (c) => c.building && !c.hasStructure,
    gesture: 'drag-up',
  },
  {
    id: 'hold',
    text: 'Hold the button to send the wave',
    when: (c) => c.building && c.hasStructure,
  },
  {
    id: 'juice',
    text: 'Tap the juice to collect it',
    when: (c) => !c.building && c.juiceOnField > 0,
  },
  {
    // Staged late on purpose: the power is the answer to a bearing you read
    // wrong, so it means nothing until there's a wave on the board to read.
    id: 'ultimate',
    text: 'Your power is charged — tap it',
    when: (c) => !c.building && c.ultReady,
    gesture: 'tap-down',
    at: 'ult',
  },
  {
    // Only once something is actually at the top of its ladder. Merging is the
    // one rule you'd never guess, and it means nothing until you're standing
    // in front of a gun that can do it.
    id: 'merge',
    text: 'Drop another on a maxed gun to change it',
    when: (c) => c.hasMaxed,
    gesture: 'tap-down',
  },
  {
    id: 'planet',
    text: 'Tap the planet to upgrade it',
    when: (c) => c.building && c.canUpgrade && c.wave > 1,
    gesture: 'tap-down',
    at: 'centre',
  },
];

export default function Coach({ game }: { game: GameHandle | null }): JSX.Element | null {
  const hud = useUi((s) => s.hud);
  const coached = useUi((s) => s.coached);
  const sheet = useUi((s) => s.sheet);

  const hasStructure = Boolean(game?.world.slots.some((s) => s.structure));

  // Building anything retires the build hint even if the sheet was opened from
  // a mount that already had something on it.
  useEffect(() => {
    if (hasStructure && !coached.build) useUi.getState().markCoached('build');
  }, [hasStructure, coached.build]);

  // Starting a wave is proof enough that the hold button was found.
  useEffect(() => {
    if (!hud.building && !coached.hold) useUi.getState().markCoached('hold');
  }, [hud.building, coached.hold]);

  useEffect(() => {
    if (hud.juice > 0 && hud.juiceOnField === 0 && !coached.juice && hud.wave > 1) {
      useUi.getState().markCoached('juice');
    }
  }, [hud.juice, hud.juiceOnField, hud.wave, coached.juice]);

  if (!game || sheet !== null) return null;

  const ctx: Ctx = {
    building: hud.building,
    hasStructure,
    juiceOnField: hud.juiceOnField,
    wave: hud.wave,
    ultReady: hud.ultCd <= 0,
    canUpgrade: hud.juice >= 8,
    hasMaxed: Boolean(
      game?.world.slots.some((s) => {
        const d = s.structure ? game.content.structures.get(s.structure.defId) : null;
        return d?.mergesInto !== undefined && isMaxed(d, s.structure!.tier);
      }),
    ),
  };
  const hint = HINTS.find((h) => !coached[h.id] && h.when(ctx));
  if (!hint) return null;

  return (
    <div className={`coach coach--${hint.at ?? hint.id}`}>
      {hint.gesture === 'drag-up' && (
        <span className="gesture gesture--drag" aria-hidden="true">
          <i />
        </span>
      )}
      {hint.gesture === 'tap-down' && (
        <span className="gesture gesture--tap" aria-hidden="true">
          <i />
        </span>
      )}
      <span className="coach-text">{hint.text}</span>
    </div>
  );
}
